import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import DatePickerField from "components/lib/Shared/DatePickerField";
import { addDays, format, subDays } from "date-fns";
import { Field } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Checkbox from "components/lib/Shared/Checkbox";

const AnnualReturns = ({
  field,
  form: {
    values,
    errors,
    touched,
    setFieldValue,
    validateField,
    setFieldTouched,
    isSubmitting,
  },
  transformedComments,
  yearOptions,
}) => {
  const { company } = useSelector((state) => state.companySlice);

  const documentData = [
    {
      label: "Annual Returns",
      name: "annualReturns",
      accept: "application/pdf",
    },
  ];

  const annualReturns = company?.annualReturns;

  // State to track availability for each financial ret
  const [availability, setAvailability] = useState(
    values?.annualReturns?.returns?.map((ret) => ({
      isAvailable: !ret.isNotAvailable,
      isNotAvailable: ret.isNotAvailable || true,
    })) || []
  );

  // Filter years to render based on selected lastAnnualReturnYear
  const selectedYear = values.annualReturns?.lastAnnualReturnYear;
  const filteredYearOptions = yearOptions.filter((option) =>
    annualReturns?.length > 0
      ? parseInt(option.value.split("/")[0]) > parseInt(selectedYear)
      : parseInt(option.value.split("/")[0]) >= parseInt(selectedYear)
  );
  // Set touched for lastAnnualReturnYear and fileUploads when values change
  useEffect(() => {
    if (values.annualReturns?.lastAnnualReturnYear) {
      setFieldTouched("annualReturns.lastAnnualReturnYear", true);
    }
    // Set touched for fileUploads when a file exists
    values.annualReturns?.returns?.forEach((_, index) => {
      const fileUploadsName = `annualReturns.returns[${index}].fileUploads`;
      const fileUploads = values.annualReturns?.returns[index]?.fileUploads;
      if (fileUploads?.annualReturns) {
        setFieldTouched(fileUploadsName, true);
        validateField(fileUploadsName);
      }
    });
  }, [
    values.annualReturns?.lastAnnualReturnYear,
    values.annualReturns?.returns,
    setFieldTouched,
    validateField,
  ]);

  const isYearDisabled = (index) => {
    // All years are initially enabled
    if (availability.every((a) => a.isNotAvailable)) {
      return false;
    }

    // Find the last "Yes" year (isAvailable: true)
    const lastYesIndex = availability.findLastIndex((a) => a.isAvailable);

    if (lastYesIndex === -1) {
      return false;
    }

    // Years before the last "Yes" are disabled
    if (index < lastYesIndex) {
      return true;
    }

    // The year immediately after the last "Yes" is enabled
    if (index === lastYesIndex + 1) {
      return false;
    }

    // Years beyond the immediate next are disabled
    if (index > lastYesIndex + 1) {
      return true;
    }

    // The last "Yes" year itself is enabled (can be toggled to "No")
    return false;
  };

  const handleAvailabilityChange = (index, isNotAvailable) => {
    const newAvailability = [...availability];
    const newReturns = [...(values?.annualReturns?.returns || [])];

    if (!isNotAvailable) {
      // USER SELECTED "YES" → Only this specific year becomes Available
      newAvailability[index] = { isAvailable: true, isNotAvailable: false };

      newReturns[index] = {
        ...newReturns[index],
        isNotAvailable: false,
        yearOfReturn: format(new Date(`${newReturns[index]?.year}`), "yyyy"),
        // Preserve existing files/dates if present
        fileUploads: newReturns[index].fileUploads || { annualReturns: null },
        dateOfReturn: newReturns[index].dateOfReturn || "",
        boardApprovalDate: newReturns[index].boardApprovalDate || "",
        auditorApprovalDate: newReturns[index].auditorApprovalDate || "",
      };

      // Reset all years after this one to "No"
      for (let i = index + 1; i < newAvailability.length; i++) {
        newAvailability[i] = { isAvailable: false, isNotAvailable: true };

        newReturns[i] = {
          ...newReturns[i],
          isNotAvailable: true,
          yearOfReturn: "",
          fileUploads: { annualReturns: null },
          dateOfReturn: "",
          boardApprovalDate: "",
          auditorApprovalDate: "",
        };
      }
    } else {
      // USER SELECTED "NO" → This year becomes Not Available
      newAvailability[index] = { isAvailable: false, isNotAvailable: true };

      newReturns[index] = {
        ...newReturns[index],
        isNotAvailable: true,
        yearOfReturn: "",
        fileUploads: { annualReturns: null },
        dateOfReturn: "",
        boardApprovalDate: "",
        auditorApprovalDate: "",
      };

      // If we're turning the last "Yes" to "No", enable all years after it
      const lastYesIndex = newAvailability.findLastIndex((a) => a.isAvailable);
      if (index === lastYesIndex) {
        // After turning this "Yes" to "No", enable all subsequent years
        for (let i = index + 1; i < newAvailability.length; i++) {
          // But don't change their actual values, just enable them
          // They will remain "No" until user explicitly changes them
        }
      }
    }

    setAvailability(newAvailability);
    setFieldValue("annualReturns.returns", newReturns);
  };

  // Check if all years are "No" (default state)
  const allNo = availability.every((a) => a.isNotAvailable);

  return (
    <div>
      <div className="flex items-baseline justify-between m-4">
        <p
          htmlFor={field.name}
          className="block mb-2 text-sm text-primary font-bold min-w-[13rem] w-full whitespace-normal"
        >
          Select the years for which you have annual return(s) filed
        </p>
      </div>
      {filteredYearOptions.map((option, index) => {
        if (!values?.annualReturns?.returns?.[index]) {
          return <div>Loading...</div>; // or loading state
        }

        const dateName = `annualReturns.returns[${index}].dateOfReturn`;
        const fileUploadsName = `annualReturns.returns[${index}].fileUploads`;

        const [yearStr, monthStr, dayStr] = option.value.split("/");
        const centralDate = new Date(`${yearStr}-${monthStr}-${dayStr}`);
        const isValidCentralDate = !isNaN(centralDate.getTime());
        // Calculate minDate  (-28 days)
        const minDate = isValidCentralDate ? subDays(centralDate, 28) : null;
        const maxDate = isValidCentralDate ? centralDate : null;

        // Check if this year is disabled
        const disabled = isYearDisabled(index);

        return (
          <div
            key={option.value}
            className="shadow-sm sm:rounded-md sm:overflow-hidden px-4 py-6 space-y-4 bg-gray-50 sm:p-6 mt-4"
          >
            <div className=" mb-4">
              <h2 className="text-4xl font-bold text-gray-800">
                {option.label.split("/")[0]}
              </h2>
              <p className="text-gray-800 font-bold">Annual Return</p>
            </div>
            <div className="mt-4">
              <DatePickerField
                label="Date of this Return"
                onChange={(date) => {
                  if (!date) {
                    setFieldValue(dateName, "");
                    return;
                  }
                  if (isNaN(date.getTime())) {
                    console.error("Invalid date:", date);
                    return;
                  }
                  const formattedDate = format(date, "yyyy/MM/dd");
                  setFieldValue(dateName, formattedDate);
                  setFieldTouched(dateName, true);
                  validateField(dateName);
                }}
                selected={
                  values?.annualReturns?.returns[index]?.dateOfReturn
                    ? new Date(
                        values?.annualReturns?.returns[index].dateOfReturn
                      )
                    : null
                }
                name={dateName}
                placeholderText="Enter Return date"
                errorPath={dateName}
                maxDate={maxDate}
                minDate={minDate}
                disabled={
                  isSubmitting ||
                  availability[index]?.isNotAvailable ||
                  disabled
                }
                showRequiredAsterisk
              />
              <ValidationError
                errorMessage={
                  errors?.annualReturns?.returns?.[index]?.dateOfReturn
                }
                isTouched={
                  touched?.annualReturns?.returns?.[index]?.dateOfReturn
                }
              />
            </div>
            <div className="flex justify-between">
              <div className="w-[85%]">
                <Field
                  name={fileUploadsName}
                  component={FileUploadForm}
                  documentData={documentData}
                  setFieldValue={(name, value) => {
                    setFieldValue(name, value);
                    setFieldTouched(name, true);
                    validateField(name);
                  }}
                  setFieldTouched={(name) => {
                    setFieldTouched(name, true);
                    validateField(name);
                  }}
                  showRequiredAsterisk
                  showModeType={values?.annualReturns?.returns?.find(
                    (el, indexValue) =>
                      indexValue === index && el?.fileUploads?.annualReturns
                  )}
                  disabled={availability[index]?.isNotAvailable || disabled}
                />
                <ValidationError
                  errorMessage={
                    errors?.annualReturns?.returns?.[index]?.fileUploads
                  }
                  isTouched={
                    touched?.annualReturns?.returns?.[index]?.fileUploads
                  }
                />
              </div>
              <div className="flex flex-col items-start justify-start">
                <p className="text-green-focused font-medium text-sm">
                  Available
                </p>
                <div className="flex gap-2">
                  <Checkbox
                    name={`availability[${index}].isAvailable`}
                    label="Yes"
                    checked={availability[index]?.isAvailable}
                    onChange={() => handleAvailabilityChange(index, false)}
                    disabled={disabled && !availability[index]?.isAvailable}
                  />
                  <Checkbox
                    name={`availability[${index}].isNotAvailable`}
                    label="No"
                    checked={availability[index]?.isNotAvailable}
                    onChange={() => handleAvailabilityChange(index, true)}
                    disabled={disabled && availability[index]?.isAvailable}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AnnualReturns;

const ValidationError = ({ errorMessage, isTouched }) => {
  if (!errorMessage || !isTouched) return null;

  return (
    <>
      <div className="text-red-500 flex items-center py-2">
        <svg
          aria-hidden="true"
          className="flex-shrink-0 inline w-5 h-5 mr-1"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          ></path>
        </svg>

        <p className="text-sm">{errorMessage}</p>
      </div>
    </>
  );
};
